import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useUpdateSetDashboardInterview } from "@/hooks/updateSets/useUpdateSetDashboardInterview";
import {
  interviewStatusOptions,
  scheduleInterviewStatusToBackgroundColor,
  interviewStatusToLabel,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { InterviewStatus, InterviewType } from "@/types";

export const DashboardInterviewStatusChip = ({
  className,
  companyId,
  interviewId,
  jobId,
  mixpanelSource = "dashboard_schedule_card",
  status: currentStatus,
  type,
}: {
  className?: string;
  companyId: string;
  interviewId: number;
  jobId: string;
  mixpanelSource?: string;
  status: InterviewStatus | null;
  type: InterviewType;
}) => {
  const [open, setOpen] = useState(false);
  const { mutate: updateInterview, isPending } =
    useUpdateSetDashboardInterview();

  const label = currentStatus
    ? interviewStatusToLabel[currentStatus]
    : "Set status";

  // "Scheduled" isn't a real concept for internal interviews, so it's hidden
  // unless one is already (wrongly) at Scheduled. Every other status is allowed.
  // Customer interviews get the full set.
  const options =
    type === InterviewType.INTERNAL
      ? interviewStatusOptions.filter(
          (o) =>
            o.value !== InterviewStatus.SCHEDULED ||
            currentStatus === InterviewStatus.SCHEDULED,
        )
      : interviewStatusOptions;

  /* Handlers */
  const handleStatusChange = (newStatus: InterviewStatus) => {
    setOpen(false);
    if (newStatus === currentStatus || isPending) return;

    mixpanelUtils.trackButtonClick(
      "Interview Status Changed",
      MIXPANEL_SCREENS.DASHBOARD,
      {
        from_status: currentStatus,
        to_status: newStatus,
        source: mixpanelSource,
      },
    );

    updateInterview({
      companyId,
      jobId,
      id: interviewId,
      status: newStatus,
    });
  };

  /* Render */
  const render = () => (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "group/chip -ml-1 h-auto w-fit gap-1 rounded px-1 py-0 text-[11px] font-medium leading-tight text-lever-text-body-on-elevation hover:bg-lever-surface-default/60",
            className,
          )}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              currentStatus
                ? scheduleInterviewStatusToBackgroundColor[currentStatus]
                : "bg-lever-surface-disabled",
            )}
          />
          <span>{label}</span>
          <ChevronDownIcon className="h-2.5 w-2.5 shrink-0 text-lever-text-label opacity-0 transition-opacity group-hover/chip:opacity-100" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-44"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {options.map((option) => (
          <DropdownMenuItem
            className="flex items-center gap-2 text-xs"
            disabled={isPending || option.value === currentStatus}
            key={option.value}
            onClick={() => {
              handleStatusChange(option.value);
            }}
          >
            <span
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                scheduleInterviewStatusToBackgroundColor[option.value],
              )}
            />
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};
